/* Листы людей ряда «Кто в кадре» (APP-SPEC-5 §4.4): «Ваши фото», «Добавьте фото», «Добавить близкого», профиль близкого.
   Запись — в аккаунт через общий peopleServer «Профиля»: память меняется только после ответа сервера. */
import { useEffect, useRef, useState } from 'react';
import type { Person, SelfProfile } from '../../../api/people';
import { uiFormat, uiText } from '../../../i18n';
import { peopleServer, type DraftPhoto, type PersonDraft } from '../../profile/people-server';
import { artworkUrl } from '../../studio/studio-art-model';
import { StudioSheet } from '../../studio/StudioSheet';

const RELATIONS = ['Муж', 'Жена', 'Сын', 'Дочь', 'Мама', 'Папа', 'Бабушка', 'Дедушка', 'Брат', 'Сестра', 'Друг', 'Подруга', 'Питомец'];
const AGES: Array<[string, string]> = [
  ['kid', 'Ребёнок'],
  ['adult', 'Взрослый'],
  ['old', 'Пожилой'],
];
const ageByRel = (rel: string) =>
  ['Сын', 'Дочь'].includes(rel) ? 'kid' : ['Бабушка', 'Дедушка'].includes(rel) ? 'old' : rel === 'Питомец' ? null : 'adult';
const MAX_PHOTOS = 5;

function usePhotoDraft(initial: DraftPhoto[]) {
  const [photos, setPhotos] = useState<DraftPhoto[]>(initial);
  const created = useRef<string[]>([]);
  useEffect(() => () => created.current.forEach(url => URL.revokeObjectURL(url)), []);
  const add = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files)
      .filter(file => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && file.size <= 10 * 1024 * 1024)
      .map(file => {
        const url = URL.createObjectURL(file);
        created.current.push(url);
        return { url, file };
      });
    setPhotos(current => [...current, ...next].slice(0, MAX_PHOTOS));
  };
  return { photos, add, remove: (index: number) => setPhotos(current => current.filter((_, i) => i !== index)) };
}

function PhotoPicker({ draft, disabled }: { draft: ReturnType<typeof usePhotoDraft>; disabled: boolean }) {
  return (
    <div className="shk-person-photos">
      <div className="shk-person-grid">
        {draft.photos.map((photo, index) => (
          <span key={photo.url}>
            {artworkUrl(photo.url) || photo.url.startsWith('blob:') ? <img src={photo.url} alt={uiFormat('Фото {0}', index + 1)} /> : null}
            <button type="button" aria-label={uiFormat('Убрать фото {0}', index + 1)} disabled={disabled} onClick={() => draft.remove(index)}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="bcu-acts">
        <label className="bcu-pl">
          {uiText('Загрузить')}
          <input
            className="studio-native-file-input"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            aria-label={uiText('Загрузить фото')}
            disabled={disabled || draft.photos.length >= MAX_PHOTOS}
            onChange={event => {
              draft.add(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
        <label className="bcu-pl">
          {uiText('Снять')}
          <input
            className="studio-native-file-input"
            type="file"
            capture="user"
            accept="image/jpeg,image/png,image/webp"
            aria-label={uiText('Снять фото')}
            disabled={disabled || draft.photos.length >= MAX_PHOTOS}
            onChange={event => {
              draft.add(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
      </div>
      <small>{uiFormat('До {0} фото: лицо крупно, при хорошем свете', MAX_PHOTOS)}</small>
    </div>
  );
}

/** «Ваши фото · Один раз для всех инструментов» */
export function SelfPhotosSheet({ self, onClose, onSaved }: { self: SelfProfile | null; onClose(): void; onSaved(): void }) {
  const draft = usePhotoDraft((self?.photos ?? []).map(photo => ({ url: photo.url, id: photo.upload_id })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <StudioSheet title="Ваши фото" onClose={onClose}>
      <h2>{uiText('Ваши фото')}</h2>
      <p>{uiText('Один раз для всех инструментов')}</p>
      <PhotoPicker draft={draft} disabled={busy} />
      <h3>{uiText('Как снимать')}</h3>
      <p>{uiText('Лицо крупно и при хорошем свете · Без сильных теней и фильтров')}</p>
      <p>{uiText('Разные ракурсы и выражения · Анфас, вполоборота, с улыбкой и без')}</p>
      {error && <p role="alert">{uiText(error)}</p>}
      <button
        type="button"
        className="btn"
        disabled={busy || !draft.photos.length}
        onClick={async () => {
          setBusy(true);
          setError('');
          try {
            await peopleServer.self({ photos: draft.photos });
            document.dispatchEvent(new CustomEvent('bot:me'));
            onSaved();
          } catch (failure) {
            setError(peopleServer.error(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        {uiText('Сохранить')}
      </button>
    </StudioSheet>
  );
}

/** «Добавьте фото» (близкий без фото), «Добавить близкого» и профиль близкого — один лист. */
export function PersonSheet({
  person,
  allowPets = true,
  onClose,
  onSaved,
}: {
  person: Person | null;
  allowPets?: boolean;
  onClose(): void;
  onSaved(saved: { id: number }): void;
}) {
  const [name, setName] = useState(person?.name ?? '');
  const [rel, setRel] = useState(person ? (person.is_pet ? 'Питомец' : person.rel) : '');
  const [age, setAge] = useState<string | null>(person?.age ?? (person ? null : 'adult'));
  const draft = usePhotoDraft((person?.photos ?? []).map(photo => ({ url: photo.url, id: photo.upload_id })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const memo = useRef<{ key?: string }>({});
  const title = !person ? 'Добавить близкого' : person.photos.length ? person.name : 'Добавьте фото';
  const ready = Array.from(name.trim()).length > 0 && Array.from(name).length <= 24 && !!rel && draft.photos.length > 0;
  return (
    <StudioSheet title={title} onClose={onClose}>
      <h2>{uiText(title)}</h2>
      {!person && <p>{uiText('Сохранится в близких')}</p>}
      {person && <p>{[person.name, person.rel].filter(Boolean).join(' · ')}</p>}
      <PhotoPicker draft={draft} disabled={busy} />
      <label className="shk-person-field">
        <span>{uiText('Имя')}</span>
        <input value={name} maxLength={48} disabled={busy} onChange={event => setName(Array.from(event.target.value).slice(0, 24).join(''))} />
      </label>
      <label className="shk-person-field">
        <span>{uiText('Кто это для вас')}</span>
        <select
          value={rel}
          disabled={busy}
          onChange={event => {
            setRel(event.target.value);
            setAge(ageByRel(event.target.value));
          }}
        >
          <option value="">{uiText('Выберите')}</option>
          {RELATIONS.filter(value => allowPets || value !== 'Питомец').map(value => (
            <option key={value} value={value}>
              {uiText(value)}
            </option>
          ))}
        </select>
      </label>
      {rel !== 'Питомец' && (
        <div className="seg sm" role="radiogroup" aria-label={uiText('Возраст')}>
          {AGES.map(([value, label]) => (
            <button key={value} type="button" role="radio" aria-checked={age === value} className={age === value ? 'on' : ''} disabled={busy} onClick={() => setAge(value)}>
              {uiText(label)}
            </button>
          ))}
        </div>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
      <button
        type="button"
        className="btn"
        disabled={busy || !ready}
        onClick={async () => {
          setBusy(true);
          setError('');
          try {
            const legacy = person
              ? { id: person.id, n: person.name, rel: person.rel, age: person.age, photos: [], version: person.version, selectable: person.selectable }
              : null;
            const body: PersonDraft = { n: name.trim(), rel, age: rel === 'Питомец' ? null : age, photos: draft.photos };
            const saved = await peopleServer.person(legacy, body, memo.current);
            document.dispatchEvent(new CustomEvent('bot:me'));
            onSaved({ id: saved.id });
          } catch (failure) {
            setError(peopleServer.error(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        {uiText(person ? 'Сохранить' : 'Добавить в кадр')}
      </button>
      {person && (
        <button
          type="button"
          className="btn line danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await peopleServer.drop(person.id);
              document.dispatchEvent(new CustomEvent('bot:me'));
              onClose();
            } catch (failure) {
              setError(peopleServer.error(failure));
            } finally {
              setBusy(false);
            }
          }}
        >
          {uiText('Удалить из близких')}
        </button>
      )}
    </StudioSheet>
  );
}
